import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { TrendChart } from '@/versions/v1/modules/_viz';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { GATE_APPROVED_SURFACE, GATE_GHOST_BUTTON, GATE_PRIMARY_BUTTON, gatePillClass } from '@/versions/v1/modules/advisory/gate-ui';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { CrossAreaFlowTable } from './CrossAreaFlowTable';
import { GmMiniRow, LandingStripTabs } from './GmLeadBand';
import { GmCard } from './GmTabs';
import { NEUTRAL_PILL } from './LandingDecisionGate';
import { limsAssayTrendChart, metProcessVarsChart, metRecoveryTrendChart } from './LandingSeries.transform';
import { limsTableRows } from './LimsTable.transform';
import { OperatingCurvePanel } from './OperatingCurvePanel';
import { StageLadder } from './StageLadder';
import {
  cmetAtStake,
  cmetCaseList,
  cmetCasesMiniRow,
  cmetHeaderFact,
  cmetLimsMiniRow,
  cmetProcessMiniRow,
  cmetRail,
  cmetStripCells,
  type CmetListRow,
  type CmetTabId,
  type RailCase,
  type RailGate,
  type RailRecord,
  type RailShows,
} from './ControlMetLanding.transform';

const PILL_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

/** Status pill on the state tokens (comp `.pill`): state colour marks state only. */
function statePill(tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral'): string {
  switch (tone) {
    case 'ok':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]');
    case 'warn':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]');
    case 'bad':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]');
    case 'info':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-info)_12%,transparent)] text-[var(--state-info)]');
    default:
      return NEUTRAL_PILL;
  }
}

const LINK = 'border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary hover:border-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

/** Case code chip (comp `.idc`). */
function CodeChip({ code }: { code: string }) {
  return (
    <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-1.5 text-[10.5px] font-medium leading-4 tracking-[0.02em] text-ink-secondary">
      {code}
    </span>
  );
}

// ---- Header fact -------------------------------------------------------------------

/** The route's one header fact (adjudication 6), set beside the page title. */
export function ControlMetHeaderFact({ state }: { state: SessionState }) {
  const fact = cmetHeaderFact(state);
  return (
    <div className="flex flex-col items-end text-right tabular-nums" data-cmet-header-fact>
      <span className="text-[10.5px] font-semibold uppercase leading-[14px] tracking-[0.08em] text-muted">{fact.label}</span>
      <span className="flex items-baseline gap-1.5 whitespace-nowrap">
        <span className="text-[17px] font-semibold leading-5 text-ink-primary">{fact.value}</span>
        <span className="text-[11.5px] text-muted">{fact.caption}</span>
      </span>
    </div>
  );
}

// ---- Lead band ---------------------------------------------------------------------

function LeadBand({
  state,
  tab,
  onTabChange,
  projectSlug,
}: {
  state: SessionState;
  tab: CmetTabId;
  onTabChange: (tab: CmetTabId) => void;
  projectSlug: string;
}) {
  const advisory = useAdvisory();
  const atStake = cmetAtStake(state, advisory, projectSlug);
  return (
    <div className="grid grid-cols-12 gap-3" data-cmet-lead-band>
      <GlassCard
        as="nav"
        tone="matte"
        aria-label="Landing views"
        className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
      >
        <LandingStripTabs
          cells={cmetStripCells(state, advisory, projectSlug)}
          selected={tab}
          onSelect={onTabChange}
          dataPrefix="cmet"
          panelId="cmet-landing-panel"
          columnsClassName="sm:grid-cols-3"
        />
      </GlassCard>
      <Link
        to={atStake.href}
        data-cmet-at-stake
        aria-label={`${atStake.label} · ${atStake.figure}${atStake.unit} · open in Advisory`}
        className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
      >
        <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{atStake.label}</span>
        <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
          <span data-cmet-at-stake-figure>{atStake.figure}</span>
          <span className="text-xs font-semibold tracking-normal text-muted">{atStake.unit}</span>
        </span>
        <span className="text-xs leading-snug tabular-nums text-muted">
          <span className="font-semibold text-ink-secondary">{atStake.comparisonLead}</span>
          {atStake.comparisonRest}
        </span>
        <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[12.5px] font-semibold text-ink-primary">
          Advisory
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </span>
      </Link>
    </div>
  );
}

// ---- Process KPI trends ------------------------------------------------------------

function ProcessView({ state, projectSlug }: { state: SessionState; projectSlug: string }) {
  const recovery = metRecoveryTrendChart(state);
  const process = metProcessVarsChart(state);
  return (
    <div className="space-y-3" data-cmet-view="kpi">
      <GmMiniRow items={cmetProcessMiniRow(state)} />
      <div className="grid grid-cols-12 items-start gap-3">
        <div className="col-span-12 min-w-0 lg:col-span-7">
          <StageLadder state={state} slug={projectSlug} />
        </div>
        <div className="col-span-12 min-w-0 lg:col-span-5">
          <OperatingCurvePanel state={state} variant="met" />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <GmCard title="7-day recovery trends vs design" className="col-span-12 lg:col-span-6" data-cmet-recovery-chart>
          <TrendChart
            kind="line"
            categories={recovery.categories}
            series={recovery.series}
            references={recovery.references}
            leftYDomain={recovery.leftYDomain}
            leftYTicks={recovery.leftYTicks}
            height={170}
            distinctSharedTones
            referenceLabelInset={10}
            referenceLabelPlacement="clear"
          />
        </GmCard>
        <GmCard title="7-day process variables vs target" className="col-span-12 lg:col-span-6" data-cmet-process-chart>
          <TrendChart
            kind="line"
            categories={process.categories}
            series={process.series}
            references={process.references}
            leftYDomain={process.leftYDomain}
            rightYDomain={process.rightYDomain}
            leftYTicks={process.leftYTicks}
            rightYTicks={process.rightYTicks}
            height={170}
            distinctSharedTones
            referenceLabelInset={10}
            referenceLabelPlacement="clear"
          />
        </GmCard>
      </div>
      <CrossAreaFlowTable state={state} collapsed />
    </div>
  );
}

// ---- Lab data (LIMS) ---------------------------------------------------------------

const TH = 'whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
const TD = 'border-b border-[var(--color-divider)] px-2 py-[7px] align-middle';

function LimsView({ state }: { state: SessionState }) {
  const rows = limsTableRows(state);
  const chart = limsAssayTrendChart(state);
  return (
    <div className="space-y-3" data-cmet-view="lims">
      <GmMiniRow items={cmetLimsMiniRow(state)} columnsClassName="sm:grid-cols-2" />
      <GmCard title="Assay trend — concentrate grades vs specification" meta="by 12 h composite" data-cmet-assay-chart>
        <TrendChart
          kind="line"
          categories={chart.categories}
          series={chart.series}
          references={chart.references}
          leftYDomain={chart.leftYDomain}
          rightYDomain={chart.rightYDomain}
          leftYTicks={chart.leftYTicks}
          rightYTicks={chart.rightYTicks}
          height={190}
          distinctSharedTones
          referenceLabelInset={10}
          referenceLabelPlacement="clear"
        />
      </GmCard>
      <GmCard title="12 h composite assay register" data-cmet-register>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px] tabular-nums">
            <thead>
              <tr>
                <th className={TH}>12 h composite</th>
                <th className={cn(TH, 'text-right')}>Pb grade %</th>
                <th className={cn(TH, 'text-right')}>Zn grade %</th>
                <th className={cn(TH, 'text-right')}>Pb in Zn %</th>
                <th className={cn(TH, 'text-right')}>Ag (oz/t)</th>
                <th className={TH}>SPC state</th>
                <th className={TH}>AI flag</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="[&:last-child>td]:border-b-0">
                  <td className={cn(TD, 'whitespace-nowrap font-semibold text-ink-primary')}>{row.composite}</td>
                  <td className={cn(TD, 'text-right text-ink-secondary')}>{row.pbGrade}</td>
                  <td className={cn(TD, 'text-right text-ink-secondary')}>{row.znGrade}</td>
                  <td
                    className={cn(TD, 'text-right', row.pbInZnTone !== 'ok' ? 'font-semibold' : 'text-ink-secondary')}
                    style={row.pbInZnTone !== 'ok' ? { color: vizToneColor(row.pbInZnTone) } : undefined}
                  >
                    {row.pbInZn}
                  </td>
                  <td className={cn(TD, 'text-right text-ink-secondary')}>{row.ag}</td>
                  <td className={TD}>
                    {/* In control is the in-band state: quiet. Only Watch takes colour. */}
                    <span className={statePill(row.spcState === 'Watch' ? 'warn' : 'neutral')}>{row.spcState}</span>
                  </td>
                  <td className={cn(TD, 'text-xs text-ink-secondary')}>{row.aiFlag ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GmCard>
    </div>
  );
}

// ---- Active AI cases ---------------------------------------------------------------

const RECORD_TONE: Record<RailRecord['status'], 'ok' | 'info' | 'neutral'> = {
  done: 'ok',
  approved: 'info',
  rejected: 'neutral',
  proposed: 'neutral',
};

function RecordLine({ record }: { record: RailRecord }) {
  return (
    <div className="mt-1.5 flex flex-col gap-[3px] text-[12.5px] text-ink-secondary" data-cmet-rail-record={record.status}>
      <div className="flex items-baseline gap-2">
        <span className={cn(statePill(RECORD_TONE[record.status]), 'flex-none')}>{record.statusLabel}</span>
        <span className="text-ink-primary">{record.text}</span>
      </div>
      {record.seat || record.detail ? (
        <span className="flex flex-wrap items-center gap-1.5 pl-0.5 text-[11.5px] text-muted">
          {record.seat ? <ActorChip actor="Human">{record.seat}</ActorChip> : null}
          {record.detail}
        </span>
      ) : null}
    </div>
  );
}

function ShowsIn({ shows }: { shows: RailShows }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5 text-xs text-ink-secondary" data-cmet-rail-shows>
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">Shows in</span>
      <ActorChip actor="Engine" />
      <span className={statePill(shows.tone)}>{shows.stateLabel}</span>
      <span className="basis-full">{shows.sentence}</span>
    </div>
  );
}

function ApprovalGate({ gate, onOpenCase }: { gate: RailGate; onOpenCase: (caseId: string) => void }) {
  const advisory = useAdvisory();
  const { can } = useRole();
  const approved = Boolean(gate.approvedStamp);
  return (
    <div
      className={cn(
        'mt-2 flex flex-col gap-1.5 rounded-xl border py-2 pl-3 pr-2 transition-colors duration-300',
        approved ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
      )}
      data-cmet-gate={approved ? 'approved' : 'open'}
    >
      <p className="text-[12.5px] text-ink-secondary">
        Proposed for today:{' '}
        {gate.items.map((item, index) => (
          <span key={item}>
            {index > 0 ? ' · ' : ''}
            <span className="font-semibold text-ink-primary">{item}</span>
          </span>
        ))}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <ActorChip actor="Agent" />
        <span className="flex-1" />
        {approved ? (
          <span className={gatePillClass('approved')} data-cmet-gate-stamp>
            {gate.approvedStamp}
          </span>
        ) : can('action:approve') ? (
          <>
            {/* A refusal needs its reason (hard rule 1): Decline opens the case, where the reason is written. */}
            <button type="button" className={cn(GATE_GHOST_BUTTON, 'h-7 px-3')} onClick={() => onOpenCase(gate.caseId)}>
              Decline
            </button>
            <button
              type="button"
              className={cn(GATE_PRIMARY_BUTTON, 'h-7 px-3')}
              onClick={() => gate.actionIds.forEach((id) => advisory.approveAction(id))}
            >
              {gate.actionIds.length > 1 ? 'Approve both' : 'Approve'}
            </button>
          </>
        ) : (
          <span className="px-2 text-xs font-medium text-muted" data-cmet-gate-waiting>
            Awaiting approval
          </span>
        )}
      </div>
    </div>
  );
}

function RailColumn({ item, onOpenCase }: { item: RailCase; onOpenCase: (caseId: string) => void }) {
  return (
    <div className="grid min-w-0 grid-cols-[18px_minmax(0,1fr)] content-start gap-x-2 gap-y-1 py-2.5 tabular-nums" data-cmet-rail-case={item.caseId}>
      <span className="pt-px text-center text-xs font-bold text-muted">{item.rank}</span>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpenCase(item.caseId)}
            className="min-w-0 text-left text-[13px] font-semibold text-ink-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
          >
            {item.title}
          </button>
          {/* The rail is Paul's Met-level issues, not Met-owned cases: the seat
              that holds each case rides beside its code (polish.P26 r1). */}
          <span className="flex shrink-0 items-center gap-1.5">
            <span data-cmet-rail-seat={item.seat}>
              <ActorChip actor="Human">{item.seat}</ActorChip>
            </span>
            <CodeChip code={item.code} />
          </span>
        </div>
        <p className="mt-px text-xs text-muted">
          <span className="font-semibold text-ink-secondary">{item.moneyLead}</span>
          {item.moneyRest}
        </p>
        {item.records.map((record) => (
          <RecordLine key={record.actionId} record={record} />
        ))}
        {item.shows ? <ShowsIn shows={item.shows} /> : null}
        {item.gate ? <ApprovalGate gate={item.gate} onOpenCase={onOpenCase} /> : null}
      </div>
    </div>
  );
}

function MetRail({ state, projectSlug, onOpenCase }: { state: SessionState; projectSlug: string; onOpenCase: (caseId: string) => void }) {
  const advisory = useAdvisory();
  const rail = cmetRail(state, advisory, projectSlug);
  return (
    <Commentable name="MetLead">
      <GmCard title="Met-level issues" meta={<span className={NEUTRAL_PILL}>{rail.count}</span>} data-cmet-rail>
        <div className="grid grid-cols-1 gap-x-[22px] md:grid-cols-3">
          {rail.cases.map((item) => (
            <RailColumn key={item.caseId} item={item} onOpenCase={onOpenCase} />
          ))}
        </div>
      </GmCard>
    </Commentable>
  );
}

const TIER_TONE: Record<string, 'bad' | 'warn' | 'neutral'> = { T1: 'bad', T2: 'warn', T3: 'warn' };

function CaseRow({ row, onOpenCase }: { row: CmetListRow; onOpenCase: (caseId: string) => void }) {
  return (
    <li
      className="grid grid-cols-[28px_210px_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 border-b border-[var(--color-divider)] py-[11px] tabular-nums last:border-b-0"
      data-cmet-case-row={row.id}
    >
      <span className={cn('pt-0.5 text-center text-xs font-bold', row.rank === 1 ? 'text-[var(--color-accent-500)]' : 'text-muted')}>
        {row.rank}
      </span>
      <div className="min-w-0">
        <p className="text-[15px] font-semibold text-ink-primary">{row.impactLabel}</p>
        <p className="text-xs text-muted">{row.moneyCaption}</p>
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-ink-primary">{row.title}</p>
        <p className="mt-0.5 text-[11.5px] text-muted">{row.driverTreePathHint}</p>
        {row.ownerBreakdown.length > 0 ? (
          <p className="mt-[3px] flex flex-wrap gap-x-3 text-xs text-muted">
            {row.ownerBreakdown.map((share) => (
              <Link key={share.issueId} to={share.href} className="hover:text-ink-primary hover:underline">
                {share.label} · {share.amountLabel}
              </Link>
            ))}
          </p>
        ) : null}
        {row.showContext ? (
          <p className="mt-[5px] max-w-[92ch] text-[12.5px] text-ink-secondary" data-cmet-case-context>
            {row.body}
          </p>
        ) : null}
        <div className="mt-1.5 flex flex-wrap gap-2.5">
          {row.caseId ? (
            <button type="button" className={LINK} onClick={() => onOpenCase(row.caseId!)}>
              Open case →
            </button>
          ) : null}
          <Link to={row.driverTreeHref} className={LINK}>
            Driver tree →
          </Link>
        </div>
      </div>
      <div className="flex items-start gap-1.5">
        <span className={statePill(TIER_TONE[row.tier] ?? 'neutral')}>{row.tier}</span>
        <span className={NEUTRAL_PILL}>{row.area}</span>
      </div>
    </li>
  );
}

function CasesView({
  state,
  projectSlug,
  onOpenCase,
}: {
  state: SessionState;
  projectSlug: string;
  onOpenCase: (caseId: string) => void;
}) {
  const rows = cmetCaseList(state, projectSlug);
  return (
    <div className="space-y-3" data-cmet-view="cases">
      <GmMiniRow items={cmetCasesMiniRow()} />
      <MetRail state={state} projectSlug={projectSlug} onOpenCase={onOpenCase} />
      <GmCard title="Active AI cases" meta="ranked by the engine" data-cmet-case-list>
        <ul>
          {rows.map((row) => (
            <CaseRow key={row.id} row={row} onOpenCase={onOpenCase} />
          ))}
        </ul>
      </GmCard>
    </div>
  );
}

// ---- Composition -------------------------------------------------------------------

/**
 * The Control metallurgist's landing (polish.P26 #713, locked comp
 * `landing-control-met.html` A, finished with C's action read and B's
 * stage-header figure): the GM landing's anatomy one role down. Three strip
 * cells are the tab bar, the at-stake link opens the one Met case priced at
 * stake, and the default view gives Paul's 4 Sep pair — the stage ladder and
 * the 7-day operating record — the panel row above the fold.
 */
export function ControlMetLanding({
  state,
  projectSlug,
  onOpenCase,
  tab: controlledTab,
  onTabChange,
}: {
  state: SessionState;
  projectSlug: string;
  onOpenCase: (caseId: string) => void;
  tab?: CmetTabId;
  onTabChange?: (tab: CmetTabId) => void;
}) {
  const [localTab, setLocalTab] = useState<CmetTabId>('kpi');
  const tab = controlledTab ?? localTab;
  const setTab = onTabChange ?? setLocalTab;
  return (
    <div className="space-y-3" data-cmet-landing>
      <Commentable name="Kpis">
        <LeadBand state={state} tab={tab} onTabChange={setTab} projectSlug={projectSlug} />
      </Commentable>
      <div id="cmet-landing-panel" role="tabpanel" aria-labelledby={`cmet-tab-${tab}`}>
        <Commentable name="Primary">
          {tab === 'kpi' ? <ProcessView state={state} projectSlug={projectSlug} /> : null}
          {tab === 'lims' ? <LimsView state={state} /> : null}
          {tab === 'cases' ? <CasesView state={state} projectSlug={projectSlug} onOpenCase={onOpenCase} /> : null}
        </Commentable>
      </div>
    </div>
  );
}
